import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, TextField, MenuItem,
  Alert, CircularProgress, Chip, Grid, FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getAiPromptTemplate, createAiPromptTemplate, updateAiPromptTemplate } from '../services/api';

const PROMPT_CATEGORIES = [
  { value: 'SearchPlan', label: 'Search Plan' },
  { value: 'Search', label: 'Search' },
  { value: 'Synthesis', label: 'Synthesis' },
  { value: 'Enrich', label: 'Enrichment' },
  { value: 'Outreach', label: 'Outreach' },
  { value: 'NextSteps', label: 'Next Steps' },
  { value: 'ImproveOutreach', label: 'Improve Outreach' },
  { value: 'TranslateOutreach', label: 'Translate Outreach' },
  { value: 'SummarizeInbound', label: 'Summarize Inbound' },
  { value: 'ClassifyIntent', label: 'Classify Intent' },
  { value: 'SuggestNextStep', label: 'Suggest Next Step' },
  { value: 'GenerateLinkedIn', label: 'Generate LinkedIn' },
];

const VARIABLE_HINTS = {
  Search: ['{{BRIEF_CONTEXT}}', '{{MAX_RESULTS}}', '{{QUERY}}', '{{SIGNAL_CONTEXT}}'],
  Outreach: ['{{BRIEF_CONTEXT}}', '{{PERSONA_CONTEXT}}', '{{SIGNAL_CONTEXT}}', '{{MESSAGING_LIBRARY}}', '{{TEMPLATE_CONTEXT}}', '{{TONE}}', '{{TONE_LOWER}}', '{{COMPANY_NAME}}', '{{CONTACT_INFO}}', '{{COMPANY_PROFILE}}'],
  Enrich: ['{{BRIEF_CONTEXT}}', '{{TEMPLATE_SCHEMA}}', '{{CURRENT_PROFILE}}', '{{COMPANY_NAME}}', '{{WEBSITE}}'],
  NextSteps: ['{{BRIEF_CONTEXT}}', '{{COMPANY_CONTEXT}}', '{{INTERACTION_CONTEXT}}'],
  SearchPlan: ['{{BRIEF_CONTEXT}}', '{{QUERY}}', '{{SIGNAL_CONTEXT}}'],
  Synthesis: ['{{BRIEF_CONTEXT}}', '{{QUERY}}'],
  ImproveOutreach: ['{{SUBJECT}}', '{{BODY}}', '{{COMPANY_NAME}}', '{{CONTACT_INFO}}'],
  TranslateOutreach: ['{{SUBJECT}}', '{{BODY}}', '{{TARGET_LANGUAGE}}'],
  SummarizeInbound: ['{{EMAIL_BODY}}', '{{SENDER_INFO}}'],
  ClassifyIntent: ['{{EMAIL_BODY}}', '{{SENDER_INFO}}'],
  SuggestNextStep: ['{{INTERACTION_HISTORY}}', '{{COMPANY_CONTEXT}}'],
  GenerateLinkedIn: ['{{COMPANY_NAME}}', '{{CONTACT_INFO}}', '{{STAGE}}', '{{BRIEF_CONTEXT}}'],
};

export default function AiPromptTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Search');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [modelOverride, setModelOverride] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await getAiPromptTemplate(id);
      setName(data.name || '');
      setCategory(data.category || 'Search');
      setDescription(data.description || '');
      setSystemPrompt(data.systemPrompt || '');
      setUserPrompt(data.userPrompt || '');
      setModelOverride(data.modelOverride || '');
      setIsDefault(data.isDefault || false);
      setIsActive(data.isActive !== false);
    } catch {
      setError('Failed to load prompt template');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadTemplate();
  }, [isNew, loadTemplate]);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!systemPrompt.trim()) { setError('System prompt is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category,
        description: description.trim(),
        systemPrompt: systemPrompt,
        userPrompt: userPrompt,
        modelOverride: modelOverride.trim() || null,
        isDefault,
        isActive,
      };
      if (isNew) {
        await createAiPromptTemplate(payload);
      } else {
        await updateAiPromptTemplate(id, payload);
      }
      navigate('/management?tab=prompts');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save prompt template');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (field, variable) => {
    if (field === 'system') {
      setSystemPrompt(prev => prev + variable);
    } else {
      setUserPrompt(prev => prev + variable);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const variables = VARIABLE_HINTS[category] || [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=prompts')} sx={{ mb: 2 }}>
        Back to Management
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isNew ? 'New AI Prompt Template' : 'Edit AI Prompt Template'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the system and user prompts that control how AI behaves for each operation.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/management?tab=prompts')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Basic Information */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">📋 Basic Information</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Identity, category, and status of this prompt template.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Name *"
              size="small"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Default Search, Custom Outreach v2"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Category *"
              size="small"
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {PROMPT_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this prompt template does and when to use it"
          sx={{ mt: 2 }}
        />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Model Override"
              size="small"
              fullWidth
              value={modelOverride}
              onChange={(e) => setModelOverride(e.target.value)}
              placeholder="Leave blank to use default model"
              helperText="Override the default AI model for this specific prompt (e.g. gemini-2.5-flash)."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1} sx={{ pt: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                label="Default for this category"
              />
              <FormControlLabel
                control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Available Variables */}
      {variables.length > 0 && (
        <Paper sx={{ p: 3, mb: 2.5 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">🔗 Available Variables</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Click a variable to insert it at the end of the system prompt. These are replaced at runtime with actual values.
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {variables.map((v) => (
              <Chip
                key={v}
                variant="outlined"
                size="small"
                clickable
                onClick={() => insertVariable('system', v)}
                label={v}
                sx={{ fontFamily: 'monospace' }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* System Prompt */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">🤖 System Prompt *</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The system-level instructions that set the AI's role, behavior, and output format.
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={18}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="You are a B2B lead research assistant..."
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.5 } } }}
          helperText={`${systemPrompt.length} characters`}
        />
      </Paper>

      {/* User Prompt */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">👤 User Prompt</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The user message template sent alongside the system prompt. Contains the actual request with context variables.
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={6}
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          placeholder="Find B2B companies matching: {{QUERY}}{{SIGNAL_CONTEXT}}"
          slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.5 } } }}
          helperText={`${userPrompt.length} characters`}
        />
      </Paper>

      {/* Bottom save bar */}
      <Paper elevation={3} sx={{
        position: 'sticky', bottom: 0, p: 2, mt: 3,
        borderTop: '1px solid', borderColor: 'divider',
        display: 'flex', justifyContent: 'flex-end', gap: 1,
      }}>
        <Button variant="outlined" onClick={() => navigate('/management?tab=prompts')}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
        </Button>
      </Paper>
    </Box>
  );
}
